import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SearchForm = ({ 
  searchType, 
  searchValue, 
  onSearchValueChange, 
  onSearch, 
  isLoading,
  error,
  className = '' 
}) => {
  const [validationError, setValidationError] = useState('');

  const validateInput = (value) => {
    if (!value?.trim()) {
      return 'Este campo es obligatorio';
    }

    if (searchType === 'cedula') {
      // Venezuelan cedula validation (10 digits max)
      const cedulaPattern = /^\d{7,10}$/;
      if (!cedulaPattern?.test(value)) {
        return 'Formato de cédula inválido. Solo números (7-10 dígitos)';
      }
    } else if (searchType === 'ticket') {
      // Ticket number validation (4-6 digits only)
      const ticketNumber = value?.trim();
      if (!/^\d{4,6}$/.test(ticketNumber)) {
        return 'Ingresa un número válido (4-6 dígitos)';
      }
    }

    return '';
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    // Only allow numbers
    const formattedValue = value.replace(/\D/g, '');
    onSearchValueChange(formattedValue);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const validation = validateInput(searchValue);
    if (validation) {
      setValidationError(validation);
      return;
    }

    setValidationError('');
    onSearch();
  };

  const getPlaceholder = () => {
    if (searchType === 'cedula') {
      return '30219848'; // ← Solo números
    }
    return '1234';
  };

  const getInputLabel = () => {
    if (searchType === 'cedula') {
      return 'Cédula (solo números)';
    }
    return 'Número de Boleto';
  };

  const formatInput = (value) => {
    if (searchType === 'cedula') {
      return value.replace(/\D/g, '').slice(0, 10); // Solo números, máx 10
    } else {
      return value.replace(/\D/g, '').slice(0, 6);  // Solo números, máx 6
    }
  };

  return (
    <div className={`bg-card rounded-xl border border-border p-6 shadow-luxury ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Icon 
              name={searchType === 'cedula' ? 'User' : 'Ticket'} 
              size={20} 
              className="text-secondary" 
            />
          </div>
          <div>
            <h3 className="font-body font-semibold text-gray-800">
              {searchType === 'cedula' ? 'Búsqueda por Cédula' : 'Búsqueda por Boleto'}
            </h3>
            <p className="text-sm text-gray-800 font-caption">
              {searchType === 'cedula' 
                ? 'Encuentra todos los boletos asociados a tu cédula' 
                : 'Verifica el estado de un boleto específico'
              }
            </p>
          </div>
        </div>

        <Input
          label={getInputLabel()}
          type="text"
          placeholder={getPlaceholder()}
          value={formatInput(searchValue)}
          onChange={handleInputChange}
          error={validationError || error}
          required
          description={
            searchType === 'cedula' 
              ? 'Formato: Solo números (7-10 dígitos). Ejemplo: 30219848'
              : 'Formato: Solo números (4-6 dígitos). Ejemplo: 1234'
          }
          className="mb-4"
        />

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Botón "Buscar Boletos" con color dorado y animaciones */}
          <Button
            type="submit"
            variant="custom"
            loading={isLoading}
            iconName="Search"
            iconPosition="left"
            className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium focus:ring-2 focus:ring-secondary/50 focus:ring-offset-2 transition-all duration-200"
            disabled={!searchValue?.trim() || isLoading}
          >
            {isLoading ? 'Buscando...' : 'Buscar Boletos'}
          </Button>

          {/* Botón "Escanear QR" con color azul oscuro base y verde al presionar */}
          <Button
            type="button"
            variant="custom"
            iconName="Camera"
            iconPosition="left"
            onClick={() => {
              // Simulate QR scanner for mobile
              if (navigator.mediaDevices && navigator.mediaDevices?.getUserMedia) {
                alert('Función de escaneo QR próximamente disponible');
              } else {
                alert('Cámara no disponible en este dispositivo');
              }
            }}
            className="sm:w-auto bg-background hover:bg-green-500 text-white font-medium focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
          >
            Escanear QR
          </Button>
        </div>

        {/* Search Tips */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-secondary mt-0.5" />
            <div>
              <h4 className="font-body font-medium text-gray-800 mb-2">
                Consejos de búsqueda:
              </h4>
              <ul className="text-sm text-gray-800 font-caption space-y-1">
                {searchType === 'cedula' ? (
                  <>
                    <li>• Ingresa tu cédula solo con números (7-10 dígitos)</li>
                    <li>• La búsqueda encuentra coincidencias parciales</li>
                    <li>• Muestra todos los boletos asociados a tu cédula</li>
                    <li>• Use el escáner QR para verificación rápida</li>
                  </>
                ) : (
                  <>
                    <li>• Ingresa solo el número del boleto (4-6 dígitos)</li>
                    <li>• No es necesario incluir "AF-2024-"</li>
                    <li>• Busca información específica de un boleto</li>
                    <li>• Use el escáner QR para verificación rápida</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchForm;